import { useEffect, useState } from 'react';
import { gql, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import {
    Button,
    Checkbox,
    Form,
    FormField,
    FormInput,
    Input,
    Label,
    Message,
    Radio,
} from 'semantic-ui-react';

import { formatDateForBirth } from '../../../dates';
import { PATHS } from '../../../paths';
import { GENDER_OPTIONS, isMinor, missingForRegistration } from '../../../utils/registration';
import { CREATE_PERSON_WITH_REGISTRATION, UPDATE_PERSON_WITH_REGISTRATION } from '../../../mutations/PERSON_REGISTRATION';
import { usePersonDocumentUpload, type PendingDocument } from '../../../hooks/usePersonDocumentUpload';

import { CommunityPicker, resolveCommunities, useCommunityData } from './CommunityPicker';
import { EmergencyContactSection, EMPTY_EMERGENCY_CONTACT, type EmergencyContactState } from './EmergencyContactSection';
import { HealthSection } from './HealthSection';

import { ConsentSection, EMPTY_CONSENT, type ConsentState } from './ConsentSection';
import { EMPTY_GUARDIAN, GuardianSection, type GuardianState } from './GuardianSection';
import { SupportingDocuments } from './SupportingDocuments';
import { useRegistrationLang, useRegistrationText } from './registrationText';
import {
    PERSON_BY_IDENTIFIER,
    PERSON_FOR_EDIT,
    POSSIBLE_EXISTING_PEOPLE,
    type PersonMatch,
} from './existingPersonQueries';

const GET_ORG_SCRIPT_LABEL = gql`
query OrganisationScriptLabel {
  me {
    organisation {
      scriptLabel
    }
  }
}`;

const GET_UPLOAD_URLS = gql`
query PersonUploadUrls($personId: Int!) {
  user(personId: $personId) {
    id
    uploadURL
    uploadURLSmall
  }
}`;

/** `yyyy-MM-dd` as a local-midnight Date for the picker — never `new Date(string)`, which is UTC. */
const dateFromDob = (dob: string | null | undefined): Date | null => {
    const m = String(dob ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const readableDob = (dob: string | null | undefined, lang: string): string => {
    const d = dateFromDob(dob);
    return d ? d.toLocaleDateString(lang === 'bn' ? 'bn-BD' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

/**
 * The registration form.
 *
 * Fields are in a **logical** order rather than the paper form's — identity, then the
 * communities someone belongs to, then what they consented to, then (for a child) who consented
 * on their behalf, then attachments. The brief explicitly released the "same order as the form"
 * constraint, and this grouping is what makes the greyed guardian section legible: it sits
 * immediately after the consents it substitutes for.
 *
 * ---------------------------------------------------------------------------------------
 * CREATE, OR EDIT SOMEONE ALREADY ENROLLED
 * ---------------------------------------------------------------------------------------
 *
 * It opens on "is this person already enrolled?". Answering yes asks for their ID number, shows
 * the name it belongs to for confirmation, loads everything on file, and the same form then
 * saves through `updatePersonWithRegistration` instead of creating a second record with a
 * second ID number. Their ID number and communities are shown but fixed.
 *
 * The create path also watches for the case where nobody asked: once a name and date of birth
 * are in, anyone already enrolled with the same birthday and a similar name is offered as
 * "edit them instead". A warning, never a block.
 *
 * Switching between the two remounts the form (see the wrapper below) — the cleanest way to be
 * sure nothing typed for one person leaks into the other's record.
 *
 * The classic form is untouched and still reachable from the toggle above.
 */

interface FormProps {
    /** Set to open straight onto editing this person. */
    editTarget: PersonMatch | null;
    onEdit: (target: PersonMatch) => void;
    onStartOver: () => void;
}

const RegistrationForm = ({ editTarget, onEdit, onStartOver }: FormProps) => {
    const navigate = useNavigate();
    const t = useRegistrationText();
    const lang = useRegistrationLang();
    const editing = editTarget != null;

    // --- already enrolled? ---
    const [alreadyEnrolled, setAlreadyEnrolled] = useState<boolean>(editing);
    const [lookupId, setLookupId] = useState('');
    const [findPerson, { data: lookupData, loading: lookingUp, called: lookupCalled, variables: lookupVariables }] =
        useLazyQuery(PERSON_BY_IDENTIFIER, { fetchPolicy: 'network-only' });
    // Hidden again by "No, search again" without another round trip.
    const [showLookupResult, setShowLookupResult] = useState(false);
    const found: PersonMatch | null = showLookupResult ? lookupData?.personByIdentifier ?? null : null;
    const lookup = () => {
        if (lookupId.trim() === '') return;
        setShowLookupResult(true);
        findPerson({ variables: { identifier: lookupId.trim() } });
    };

    // --- identity ---
    const [name, setName] = useState('');
    const [preferredName, setPreferredName] = useState('');
    const [scriptName, setScriptName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [dob, setDob] = useState<Date | null>(null);
    const [graphqlDob, setGraphqlDob] = useState('');
    const [gender, setGender] = useState('');
    const [photoFile, setPhotoFile] = useState<File | null>(null);

    /**
     * Whether picture permission has been answered in its own right.
     *
     * Lives here rather than in `ConsentSection` so it survives a re-render of that component,
     * and is deliberately not sent to the server: it describes how this form was filled in,
     * not a fact about the person. When editing it starts true — the stored answer is an
     * answer, and must not be overwritten by the consent toggle above it.
     */
    const [picturePermissionTouched, setPicturePermissionTouched] = useState(editing);

    /** Answers the phone question for someone who has none, so it does not block them. */
    const [noPhone, setNoPhone] = useState(false);

    // --- communities & identifier ---
    // One list, in pick order. The identifier is *derived* from it — never typed or picked — so
    // there is no first box for a programme to land in. See CommunityPicker.
    const [communityIds, setCommunityIds] = useState<number[]>([]);
    const [homeLocationId, setHomeLocationId] = useState<number | null>(null);
    const communityData = useCommunityData();
    const { coding, identifier } = resolveCommunities(communityData, communityIds, homeLocationId);

    // --- emergency contact & health ---
    const [emergency, setEmergency] = useState<EmergencyContactState>(EMPTY_EMERGENCY_CONTACT);
    const [healthInformation, setHealthInformation] = useState('');

    // --- consent & guardian ---
    const [consent, setConsent] = useState<ConsentState>(EMPTY_CONSENT);
    const [guardian, setGuardian] = useState<GuardianState>(EMPTY_GUARDIAN);

    // --- documents ---
    const [documents, setDocuments] = useState<PendingDocument[]>([]);

    const [showErrors, setShowErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState('');

    const [createPersonMutation, { loading: creating }] = useMutation(CREATE_PERSON_WITH_REGISTRATION);
    const [updatePersonMutation, { loading: updating }] = useMutation(UPDATE_PERSON_WITH_REGISTRATION);
    const loading = creating || updating;
    const [fetchUploadUrls] = useLazyQuery(GET_UPLOAD_URLS, { fetchPolicy: 'network-only' });
    const uploadDocuments = usePersonDocumentUpload();

    const { data: orgData } = useQuery(GET_ORG_SCRIPT_LABEL);
    const scriptLabel = orgData?.me?.organisation?.scriptLabel || 'Bengali';

    // ------------------------------------------------------------ editing: load what is on file
    const { data: personData, loading: loadingPerson } = useQuery(PERSON_FOR_EDIT, {
        variables: { personId: editTarget?.personId },
        skip: !editing,
        fetchPolicy: 'network-only',
    });
    const person = personData?.user;

    useEffect(() => {
        if (person == null) return;
        const r = person.registration ?? {};
        const born = dateFromDob(person.dob);

        setName(person.name ?? '');
        setPreferredName(person.preferredName ?? '');
        setScriptName(person.scriptName ?? '');
        setPhone(person.phone ?? '');
        setAddress(person.address ?? '');
        setDob(born);
        setGraphqlDob(born ? formatDateForBirth(born.getTime()) : '');
        setGender(person.gender ?? '');
        setConsent({
            ...EMPTY_CONSENT,
            picturePermission: !!person.picturePermission,
            consentDataCollection: !!r.consentDataCollection,
            healthSafetyBriefed: !!r.healthSafetyBriefed,
            consentHealthSafety: !!r.consentHealthSafety,
            idSightedByCM: !!r.idSightedByCM,
        });
        setGuardian({
            guardianName: r.guardianName ?? '',
            guardianDob: r.guardianDob ?? '',
            guardianRelationship: r.guardianRelationship ?? '',
            guardianPhone: r.guardianPhone ?? '',
            guardianIsMinorNoAlternative: !!r.guardianIsMinorNoAlternative,
            guardianConsentsToChildData: !!r.guardianConsentsToChildData,
            guardianIdSightedConfirmsRelationship: !!r.guardianIdSightedConfirmsRelationship,
            guardianNoIdButConfirmed: !!r.guardianNoIdButConfirmed,
        });
        setEmergency({
            emergencyContactName: r.emergencyContactName ?? '',
            emergencyContactPhone: r.emergencyContactPhone ?? '',
            emergencyContactRelationship: r.emergencyContactRelationship ?? '',
        });
        setHealthInformation(r.healthInformation ?? '');
    }, [person]);

    // Their communities, as the picker's options name them. Read-only while editing.
    const optionIds = new Set(communityData.options.map((o) => o.id));
    const theirCommunities: string[] = (person?.tags ?? [])
        .filter((tag: any) => optionIds.has(Number(tag.id)))
        .map((tag: any) => tag.name);

    // Only offered when they may change it; otherwise left out of the save, so never blanked.
    const healthEditable = !editing || editTarget!.canEditHealthInformation;

    // ------------------------------------------------------------ creating: likely duplicates
    // Debounced, so typing a name is one lookup rather than one per keystroke.
    const [settledName, setSettledName] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setSettledName(name.trim()), 600);
        return () => clearTimeout(timer);
    }, [name]);

    const { data: duplicateData } = useQuery(POSSIBLE_EXISTING_PEOPLE, {
        variables: { name: settledName, dob: graphqlDob },
        skip: editing || alreadyEnrolled || settledName.length < 3 || graphqlDob === '',
        fetchPolicy: 'network-only',
    });
    const possibleDuplicates: PersonMatch[] = (!editing && !alreadyEnrolled && duplicateData?.possibleExistingPeople) || [];

    // ------------------------------------------------------------ validation
    // What this form requires — in the form only. The server stays as permissive as it was,
    // because the classic form shares `adminCreateUser` and must keep saving unchanged. See
    // `missingForRegistration` for what is and is not on the list, and why.
    const personIsMinor = isMinor(dob);
    const missing = missingForRegistration({
        name,
        dob: graphqlDob,
        // An existing person's communities are fixed, so there is nothing to pick.
        coding: editing ? 'ok' : coding.status,
        phone,
        noPhone,
        emergencyContactName: emergency.emergencyContactName,
        emergencyContactPhone: emergency.emergencyContactPhone,
        consentDataCollection: consent.consentDataCollection,
        healthSafetyBriefed: consent.healthSafetyBriefed,
        consentHealthSafety: consent.consentHealthSafety,
        isMinor: personIsMinor,
        guardianConsentsToChildData: guardian.guardianConsentsToChildData,
    }, t.missing);
    const isComplete = missing.length === 0;

    const uploadPhoto = async (personId: number) => {
        if (!photoFile) return;
        try {
            const { data } = await fetchUploadUrls({ variables: { personId } });
            const urls = [data?.user?.uploadURL, data?.user?.uploadURLSmall].filter(Boolean) as string[];
            await Promise.all(urls.map((url) => fetch(url, {
                method: 'PUT',
                body: photoFile,
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Length': photoFile.size.toString(),
                },
                referrer: 'no-referrer',
            })));
        } catch (photoErr) {
            // The person was saved successfully; only the photo failed. Don't block navigation.
            console.error('Photo upload failed', photoErr);
        }
    };

    /** The registration answers both paths send. */
    const registrationVariables = () => ({
        // The three retired data-use columns are deliberately not sent: the server derives them
        // from `consentDataCollection`, so sending them would be a second source for one answer.
        consentDataCollection: consent.consentDataCollection,
        healthSafetyBriefed: consent.healthSafetyBriefed,
        consentHealthSafety: consent.consentHealthSafety,
        idSightedByCM: consent.idSightedByCM,
        ...guardian,
        ...emergency,
    });

    const handleSave = async () => {
        if (!isComplete) {
            setShowErrors(true);
            return;
        }

        setErrorMessage('');
        setWarningMessage('');

        try {
            let personId: number;

            if (editing) {
                const result = await updatePersonMutation({
                    variables: {
                        personId: editTarget!.personId,
                        name,
                        preferredName,
                        scriptName,
                        phone: noPhone ? '' : phone,
                        address,
                        dob: graphqlDob,
                        gender: gender || null,
                        picturePermission: consent.picturePermission,
                        ...registrationVariables(),
                        // Blank clears it when they may edit it; absent leaves it alone when not.
                        ...(healthEditable ? { healthInformation: healthInformation.trim() } : {}),
                    },
                });
                personId = result.data.updatePersonWithRegistration.id;
            } else {
                const result = await createPersonMutation({
                    variables: {
                        name,
                        preferredName,
                        address,
                        scriptName,
                        identifier,
                        phone,
                        dob: graphqlDob,
                        gender: gender || null,
                        // The community that codes the identifier first, then the rest. The server
                        // checks the identifier against the whole set, not the order.
                        tagIds: coding.status === 'ok'
                            ? [coding.community.id, ...communityIds.filter((id) => id !== coding.community.id)]
                            : communityIds,
                        picturePermission: consent.picturePermission,
                        // Only this form sends it, and it is what stamps `completedAt` — people
                        // added on the classic form deliberately get no registration row at all.
                        registrationCompleted: true,
                        ...registrationVariables(),
                        healthInformation: healthInformation.trim() || null,
                    },
                });
                personId = result.data.adminCreateUser.id;
            }

            await uploadPhoto(personId);

            const failed = await uploadDocuments(personId, documents);

            if (failed.length > 0) {
                // The person is saved. Say which attachments did not make it **and why**, and
                // still go to the profile — they can be re-added there.
                setWarningMessage(t.documentsFailed(failed.map((f) => `${f.fileName}: ${f.reason}`).join(' · ')));
            }

            navigate(`/people/${personId}`);
        } catch (e: any) {
            setErrorMessage(e?.message || t.saveFailed);
        }
    };

    // ------------------------------------------------------------ "already enrolled?" step
    const lookedUpId = String(lookupVariables?.identifier ?? '');
    const enrolledQuestion = !editing && (
        <div className="creator-section">
            <FormField>
                <label>{t.enrolledQuestion}</label>
                <div className="sex-options">
                    <Radio
                        id="enrolled-no"
                        name="alreadyEnrolled"
                        label={t.enrolledNo}
                        checked={!alreadyEnrolled}
                        onChange={() => setAlreadyEnrolled(false)}
                    />
                    <Radio
                        id="enrolled-yes"
                        name="alreadyEnrolled"
                        label={t.enrolledYes}
                        checked={alreadyEnrolled}
                        onChange={() => setAlreadyEnrolled(true)}
                    />
                </div>
            </FormField>

            {alreadyEnrolled && (
                <>
                    <FormField>
                        <label htmlFor="lookup-identifier">{t.idNumberLabel}</label>
                        <Input
                            id="lookup-identifier"
                            placeholder={t.idNumberPlaceholder}
                            value={lookupId}
                            onChange={(e) => setLookupId(e.target.value)}
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    lookup();
                                }
                            }}
                            action={{
                                type: 'button',
                                content: t.find,
                                loading: lookingUp,
                                disabled: lookupId.trim() === '' || lookingUp,
                                onClick: lookup,
                            }}
                        />
                    </FormField>

                    {showLookupResult && lookupCalled && !lookingUp && found == null && (
                        <Message warning visible content={t.notFound(lookedUpId)} />
                    )}
                    {found != null && !found.canEdit && (
                        <Message warning visible content={t.cannotEdit(found.name ?? lookedUpId)} />
                    )}
                    {/* Confirmed by name before anything loads — an ID number typed one digit out
                        would otherwise open somebody else's record ready to be overwritten. */}
                    {found != null && found.canEdit && (
                        <Message info visible>
                            <Message.Header>{t.confirmTitle}</Message.Header>
                            <p>
                                <strong>{found.identifier} — {found.name}</strong>
                                {found.dob && <> · {t.bornOn(readableDob(found.dob, lang))}</>}
                            </p>
                            <Button type="button" primary size="small" onClick={() => onEdit(found)}>
                                {t.loadDetails}
                            </Button>
                            <Button
                                type="button"
                                basic
                                size="small"
                                onClick={() => {
                                    setLookupId('');
                                    setShowLookupResult(false);
                                }}
                            >
                                {t.notThem}
                            </Button>
                        </Message>
                    )}
                </>
            )}
        </div>
    );

    // Answered "yes" but not loaded anyone yet: nothing below applies until they have.
    if (!editing && alreadyEnrolled) {
        return <Form size="small">{enrolledQuestion}</Form>;
    }

    if (editing && (loadingPerson || person == null)) {
        return <p>{t.loadingPerson}</p>;
    }

    return (
        <Form error={!!errorMessage} size="small">
            {enrolledQuestion}

            {editing && (
                <Message info visible>
                    <p>{t.editingBanner(person.name ?? '', editTarget!.identifier ?? '')}</p>
                    <Button type="button" basic size="small" onClick={onStartOver}>{t.startOver}</Button>
                </Message>
            )}

            {/* ------------------------------------------------------------- identity */}
            <div className="creator-section">
                <div className="creator-section-title">{t.identity}</div>

                {editing ? (
                    <>
                        <FormField>
                            <label>{t.communities}</label>
                            <div>
                                {theirCommunities.length > 0
                                    ? theirCommunities.map((c) => <Label key={c} style={{ marginBottom: '0.3em' }}>{c}</Label>)
                                    : '—'}
                            </div>
                        </FormField>
                        <FormField>
                            <label htmlFor="registration-identifier">{t.identifier}</label>
                            <Input
                                id="registration-identifier"
                                fluid
                                readOnly
                                className="identifier-display"
                                value={person.identifier ?? editTarget!.identifier ?? ''}
                            />
                            <div className="consent-hint">{t.communitiesLocked}</div>
                        </FormField>
                    </>
                ) : (
                    <CommunityPicker
                        data={communityData}
                        selectedIds={communityIds}
                        onSelectedIdsChange={setCommunityIds}
                        homeLocationId={homeLocationId}
                        onHomeLocationIdChange={setHomeLocationId}
                        showErrors={showErrors}
                    />
                )}

                <Form.Group widths="equal">
                    <FormInput
                        error={showErrors && name.length < 1 && t.nameError}
                        fluid
                        label={t.name}
                        placeholder={t.name}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <FormInput
                        fluid
                        label={t.preferredName}
                        placeholder={t.preferredName}
                        value={preferredName}
                        onChange={(e) => setPreferredName(e.target.value)}
                    />
                </Form.Group>

                <Form.Group widths="equal">
                    <FormInput
                        fluid
                        label={t.nameInScript(scriptLabel)}
                        placeholder=""
                        value={scriptName}
                        onChange={(e) => setScriptName(e.target.value)}
                    />
                    <FormField error={showErrors && phone.trim() === '' && !noPhone}>
                        <label htmlFor="registration-phone">{t.phone}</label>
                        <input
                            id="registration-phone"
                            type="text"
                            placeholder={t.phonePlaceholder}
                            disabled={noPhone}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                        {/* An answer, not a skip: "has no phone" is a fact about them, and without
                            it the field would block registering someone who genuinely has none. */}
                        <Checkbox
                            id="registration-no-phone"
                            label={t.noPhone}
                            checked={noPhone}
                            onChange={(_, d) => {
                                setNoPhone(!!d.checked);
                                if (d.checked) setPhone('');
                            }}
                            style={{ marginTop: '0.5em' }}
                        />
                    </FormField>
                </Form.Group>

                <Form.Group widths="equal">
                    <FormField error={showErrors && graphqlDob.length < 1}>
                        <label>{t.dob}</label>
                        <DatePicker
                            selected={dob}
                            dateFormat="dd/MM/yyyy"
                            onChange={(date) => {
                                setGraphqlDob(date != null ? formatDateForBirth(date?.getTime()) : '');
                                setDob(date);
                            }}
                            showYearDropdown
                            maxDate={new Date()}
                        />
                        {showErrors && graphqlDob.length < 1 && (
                            <div className="ui pointing above prompt label">{t.dobError}</div>
                        )}
                    </FormField>
                    <FormField>
                        <label>{t.gender}</label>
                        <div className="sex-options">
                            {GENDER_OPTIONS.map((option) => (
                                <Radio
                                    key={option.value}
                                    // Semantic only emits `id`/`htmlFor` when given an id; without
                                    // one the label is not associated with the input.
                                    id={`gender-${option.value}`}
                                    label={t.genders[option.value] ?? option.label}
                                    name="genderRadioGroup"
                                    value={option.value}
                                    checked={gender === option.value}
                                    onChange={() => setGender(option.value)}
                                />
                            ))}
                        </div>
                    </FormField>
                </Form.Group>

                {/* Probably already enrolled: say so before a second record and ID number exist. */}
                {possibleDuplicates.length > 0 && (
                    <Message warning visible>
                        <Message.Header>{t.possibleMatchTitle}</Message.Header>
                        <p>{t.possibleMatchBody}</p>
                        {possibleDuplicates.map((match) => (
                            <div key={match.personId} style={{ marginBottom: '0.4em' }}>
                                <strong>{match.identifier} — {match.name}</strong>
                                {match.dob && <> · {t.bornOn(readableDob(match.dob, lang))}</>}{' '}
                                {match.canEdit
                                    ? <Button type="button" size="mini" onClick={() => onEdit(match)}>{t.editThemInstead}</Button>
                                    : <em>({t.noPermissionShort})</em>}
                            </div>
                        ))}
                    </Message>
                )}

                {/* Optional since 2026-11-03 — it was required while phone was not, which is the
                    wrong way round for reaching someone. */}
                <FormInput
                    fluid
                    label={t.address}
                    placeholder={t.address}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                />

                <FormField>
                    <label>{t.photo}</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                    />
                    {editing && <div className="consent-hint">{t.photoKeepHint}</div>}
                </FormField>
            </div>

            {/* -------------------------------------------------------------- consent */}
            <ConsentSection
                value={consent}
                onChange={setConsent}
                picturePermissionTouched={picturePermissionTouched}
                onPicturePermissionTouched={() => setPicturePermissionTouched(true)}
            />

            {/* ------------------------------------------------------------- guardian */}
            <GuardianSection value={guardian} onChange={setGuardian} personDob={dob} />

            {/* ---------------------------------------------------- emergency contact */}
            {/* After the guardian, so "Same as parent/guardian" has something above it to copy. */}
            <EmergencyContactSection
                value={emergency}
                onChange={setEmergency}
                guardian={guardian}
                personIsMinor={personIsMinor}
                showErrors={showErrors}
            />

            {/* --------------------------------------------------------------- health */}
            {/* Hidden, not emptied, for someone who may not see it: an empty box would read as
                "nothing on file" and the save would wipe what a social worker recorded. */}
            {healthEditable && <HealthSection value={healthInformation} onChange={setHealthInformation} />}

            {/* ------------------------------------------------------------ documents */}
            <SupportingDocuments
                documents={documents}
                onChange={setDocuments}
                disabled={loading}
                alreadyOnFile={editing ? (person?.supportingDocuments ?? []).length : undefined}
            />

            {/* Why it will not save, in the form's own order. Shown once they have tried, so the
                list answers "why didn't that work" rather than being a wall before they start. */}
            {showErrors && !isComplete && (
                <Message warning visible header={t.stillNeeded} list={missing} />
            )}

            <Message error header={t.couldNotSave} content={errorMessage} />
            {warningMessage && <Message warning visible content={warningMessage} />}

            {/* Enabled while incomplete on purpose: clicking is what shows the list above and the
                field highlights. A disabled button with no reason is the thing this replaces. */}
            <Button
                primary
                type="button"
                loading={loading}
                disabled={loading}
                onClick={() => handleSave()}
            >
                {editing ? t.saveChanges : t.save}
            </Button>
            <Button
                type="button"
                basic
                disabled={loading}
                onClick={() => navigate(PATHS.PEOPLE)}
            >
                {t.cancel}
            </Button>
        </Form>
    );
};

/**
 * Holds which person, if any, is being edited, and remounts the form whenever that changes.
 * A fresh form per person is what guarantees one person's half-typed answers can never be saved
 * onto another's record.
 */
const NewPersonCreator = ({ onEditingChange }: { onEditingChange?: (editing: boolean) => void }) => {
    const [session, setSession] = useState<{ key: number; target: PersonMatch | null }>({ key: 0, target: null });

    const open = (target: PersonMatch | null) => {
        setSession((s) => ({ key: s.key + 1, target }));
        onEditingChange?.(target != null);
        window.scrollTo?.(0, 0);
    };

    return (
        <RegistrationForm
            key={session.key}
            editTarget={session.target}
            onEdit={(target) => open(target)}
            onStartOver={() => open(null)}
        />
    );
};

export { NewPersonCreator };
